import type { VideoPlayer } from '@zoom/videosdk';
import { TFunction } from 'i18next';
import { useAtomValue } from 'jotai';
import { RefObject } from 'react';
import { useMatch } from 'react-router-dom';

import { useTranslate } from '@growtherapy/i18n';
import { AvatarAccent, InitialsAvatar, Text, cn } from '@growtherapy/sprout-ui';

import { ActiveSpeakerOutline, NameBadge } from '../../components';
import { PROVIDER_PATH } from '../../config';
import { nonEssentialElementProps } from '../../hooks';
import { useVideoPlatform } from '../../hooks/useVideoPlatform';
import { UserType } from '../../types';
import { nameToInitials } from '../../utils';
import {
  VideoPlatform,
  activelySpeakingParticipantIdsAtom,
  isOwnAudioOffAtom,
  isOwnVideoOffAtom,
  isScreenShareModeAtom,
  parseIdentity,
  useRemoteVideoComponent,
} from '../../video-sdk';
import { useRemoteDataComponent } from '../../video-sdk/actions/useComponentActions';
import { Participant } from '../../video-sdk/core/room/participant/Participant';
import { remoteParticipantConnectionStatesByIdAtom } from '../../video-sdk/core/room/participant/atoms';
import { ParticipantConnectionState } from '../../video-sdk/core/room/participant/types';
import { remoteMediaStatusByParticipantIdAtom, userDataFamily } from '../../video-sdk/twilio';
import { isWhiteboardModeAtom } from '../messages/state';
import { UseRemoteMediaProps, useRemoteMedia } from '../useRemoteMedia';
import { RemoteParticipantReconnectingOverlayWrapper } from './reconnect/RemoteParticipantReconnectingOverlay';
import { useTriggerPictureInPicture } from './useTriggerPictureInPicture';

export type NameBadgeWrapperProps = {
  className?: string;
  participant: Participant;
};

function NameBadgeWrapper({ className, participant }: NameBadgeWrapperProps) {
  const participantName = participant.displayName;
  const audioStatus = useAtomValue(remoteMediaStatusByParticipantIdAtom)[participant.identity];
  const userData = useAtomValue(userDataFamily(participant.identity));

  return (
    <NameBadge
      audioMuted={audioStatus?.isAudioMuted ?? true}
      name={participantName}
      pronouns={userData?.pronouns}
      className={className}
      isSelf={false}
    />
  );
}

export type RemoteParticipantProps = {
  className?: string;
  isProviderView?: boolean;
  isVideoOff?: boolean;
  isOwnAudioOff?: boolean;
  isOwnVideoOff?: boolean;
  isScreenShareMode?: boolean;
  isWhiteboardMode?: boolean;
  isSpeaking?: boolean;
  videoRef: RefObject<HTMLVideoElement | VideoPlayer>;
  audioRef: RefObject<HTMLAudioElement>;
  nameBadgeComponent: typeof NameBadgeWrapper;
  remoteParticipantReconnectingOverlayComponent: typeof RemoteParticipantReconnectingOverlayWrapper;
  isReconnecting?: boolean;
} & UseRemoteMediaProps;

export type RemoteParticipantWrapperProps = Pick<RemoteParticipantProps, 'className'> & UseRemoteMediaProps;

function getVideoOffMessaging(
  t: TFunction,
  isProviderView: boolean,
  remoteParticipantIsProvider: boolean,
  isOwnVideoOff?: boolean,
  isOwnAudioOff?: boolean,
) {
  const canSee = !isOwnVideoOff;
  const canHear = !isOwnAudioOff;

  if (isProviderView) {
    if (canSee && canHear) return t('telehealth-core:video.yourClientHasCameraOffButCanStillSeeAndHear');
    if (canSee) return t('telehealth-core:video.yourClientHasCameraOffButCanStillSee');
    if (canHear) return t('telehealth-core:video.yourClientHasCameraOffButCanStillHear');
    return t('telehealth-core:video.yourClientHasCameraOff');
  }

  if (remoteParticipantIsProvider) {
    if (canSee && canHear) return t('telehealth-core:video.yourProviderHasCameraOffButCanStillSeeAndHear');
    if (canSee) return t('telehealth-core:video.yourProviderHasCameraOffButCanStillSee');
    if (canHear) return t('telehealth-core:video.yourProviderHasCameraOffButCanStillHear');
    return t('telehealth-core:video.yourProviderHasCameraOff');
  }

  if (canSee && canHear) return t('telehealth-core:video.thisPersonHasCameraOffButCanStillSeeAndHear');
  if (canSee) return t('telehealth-core:video.thisPersonHasCameraOffButCanStillSee');
  if (canHear) return t('telehealth-core:video.thisPersonHasCameraOffButCanStillHear');
  return t('telehealth-core:video.thisPersonHasCameraOff');
}

export function RemoteParticipantCell({
  audioRef,
  className,
  isOwnAudioOff,
  isOwnVideoOff,
  isProviderView,
  isScreenShareMode,
  isWhiteboardMode,
  isSpeaking,
  isVideoOff,
  nameBadgeComponent: NameBadgeComponent,
  remoteParticipant,
  videoRef,
  isReconnecting,
  remoteParticipantReconnectingOverlayComponent: RemoteParticipantReconnectingOverlayComponent,
}: RemoteParticipantProps) {
  const t = useTranslate();
  const RemoteVideo = useRemoteVideoComponent();
  const { id, userType } = parseIdentity(remoteParticipant?.identity);
  const participantName = remoteParticipant?.displayName;

  const remoteParticipantIsProvider = userType === UserType.PROVIDER;
  const displayInitials = participantName ? nameToInitials(participantName) : '';

  const isPresentationMode = isScreenShareMode || isWhiteboardMode;
  const showVideoOffMessaging = !isPresentationMode;
  const videoOffMessaging = getVideoOffMessaging(
    t,
    !!isProviderView,
    remoteParticipantIsProvider,
    isOwnVideoOff,
    isOwnAudioOff,
  );

  const videoPlatformIsZoom = useVideoPlatform() === VideoPlatform.Zoom;

  return (
    <div
      className={cn(
        'relative overflow-hidden bg-neutral-900 rounded-2xl flex justify-center items-center',
        'rebrand:bg-surface-inverse',
        className,
      )}
      style={{
        // Clips reconnect overlay bg blur by rounded border
        clipPath: 'inset(0 round 16px)',
      }}
      data-testid={`remote-audio-video:${id}`}
      /* 
         For accessibility: The aria-label and aria-labelledby attributes are not well supported 
         on the HTML5 <audio> and <video> elements at this time. In the meantime, we can 
         add the label to the parent element.
        */
      role='region'
      aria-label={t('telehealth-core:video.liveRemoteParticipantVideoAndAudio')}
    >
      {isVideoOff && !isReconnecting && (
        <div className='absolute isolate flex flex-col items-center'>
          <InitialsAvatar
            initials={displayInitials}
            size={isPresentationMode ? 'sm' : 'md'}
            className={cn('text-center flex items-center justify-center')}
            accent={AvatarAccent.Green}
          />
          {showVideoOffMessaging && (
            <Text
              variant={isPresentationMode ? 'xs' : 'sm'}
              className='text-neutral-600 p-4 text-center rebrand:text-text-inverse'
              data-testid='video-off-messaging'
            >
              {videoOffMessaging}
            </Text>
          )}
        </div>
      )}

      <RemoteVideo remoteParticipantId={id} audioRef={audioRef} videoRef={videoRef} />

      <RemoteParticipantReconnectingOverlayComponent remoteParticipantIdentity={remoteParticipant.identity} />

      {participantName && (
        <NameBadgeComponent
          participant={remoteParticipant}
          className={cn(`absolute bottom-0 left-0 ${nonEssentialElementProps.className}`, {
            'z-[1]': videoPlatformIsZoom,
          })}
        />
      )}
      <ActiveSpeakerOutline isOutlined={!!isSpeaking} />
    </div>
  );
}

export function RemoteParticipantWrapper({
  remoteParticipant,
  className,
  ...listeners
}: RemoteParticipantWrapperProps) {
  const { audioRef, videoRef } = useRemoteMedia({
    remoteParticipant,
    ...listeners,
  });
  const isOwnAudioOff = useAtomValue(isOwnAudioOffAtom);
  const isOwnVideoOff = useAtomValue(isOwnVideoOffAtom);
  const remoteMediaStatusByParticipantId = useAtomValue(remoteMediaStatusByParticipantIdAtom);
  const videoDelegatorStatus = remoteMediaStatusByParticipantId[remoteParticipant.identity ?? ''];
  const isVideoOff = videoDelegatorStatus?.isVideoOff ?? true;
  const isScreenShareMode = useAtomValue(isScreenShareModeAtom);
  const isWhiteboardMode = useAtomValue(isWhiteboardModeAtom);

  const isSpeaking = useAtomValue(activelySpeakingParticipantIdsAtom).has(remoteParticipant.identity);
  const isProviderView = !!useMatch(PROVIDER_PATH);
  const connectionState = useAtomValue(remoteParticipantConnectionStatesByIdAtom)[remoteParticipant.identity];
  const isReconnecting = connectionState === ParticipantConnectionState.Reconnecting;
  const RemoteData = useRemoteDataComponent();

  useTriggerPictureInPicture(remoteParticipant?.identity ?? '', videoRef);

  return (
    <>
      <RemoteData remoteParticipant={remoteParticipant} {...listeners} />
      <RemoteParticipantCell
        audioRef={audioRef}
        className={className}
        isOwnAudioOff={isOwnAudioOff}
        isOwnVideoOff={isOwnVideoOff}
        isProviderView={isProviderView}
        isScreenShareMode={isScreenShareMode}
        isWhiteboardMode={isWhiteboardMode.value}
        isSpeaking={isSpeaking}
        isVideoOff={isVideoOff}
        nameBadgeComponent={NameBadgeWrapper}
        remoteParticipant={remoteParticipant}
        videoRef={videoRef}
        isReconnecting={isReconnecting}
        remoteParticipantReconnectingOverlayComponent={RemoteParticipantReconnectingOverlayWrapper}
      />
    </>
  );
}
